<script setup lang="ts">
import { onUnmounted, type PropType, ref, computed, type Ref, watch } from 'vue';
import DeviceMeta from '../DeviceMeta';
import { type MetaProps } from '../DeviceMeta/lib';
import OneCircledIcon from './components/OneCircledIcon.vue';
import TwoCircledIcon from './components/TwoCircledIcon.vue';
import { useI18n } from 'vue-i18n';

type State = Array<Channel>;

type Channel = {
  actual: boolean;
  target: boolean;
  reset: boolean;
  error: boolean;
}

type PollStatus = {
  timer: ReturnType<typeof setInterval>;
  count: number;
}

const POLL_INTERVAL = 10000; // Poll every tenth second
const RESET_DELAY = 1500;
const POLL_COUNT = 10;

const { t } = useI18n();

const props = defineProps({
  channels: {
    type: Array as PropType<Array<boolean>>,
    required: true
  },
  onPoll: {
    type: Function as PropType<() => Promise<Array<boolean>>>,
    required: true
  },
  meta: {
    type: Object as PropType<MetaProps>,
    default: undefined,
  },
});

const emit = defineEmits(['setState']);

const state = ref<State>([]);
const pollStatus = ref<PollStatus>();
const channelIndex = ref<number>(0);
const currentState = computed(() => state.value?.[channelIndex.value]);
const isPending = ref(false);

const clearTimer = () => {
  if (pollStatus.value) {
    clearInterval(pollStatus.value.timer);
    pollStatus.value = undefined;
    isPending.value = false;
  }
};

const pollingEnable = () => {
  const timer = setInterval(updateState, POLL_INTERVAL);
  const count = POLL_COUNT;

  pollStatus.value = {
    timer,
    count
  };
};

const updateState = async () => {
  if (!pollStatus.value) return;

  try {
    const pollState = await props.onPoll();

    pollState.forEach((value, index) => {
      const channel = state.value[index];
      if (channel) channel.actual = value;
    });

    // Check if all values is according to target
    const confirm = state.value.reduce((acc, s) => acc && (s.actual === s.target), true);

    if (confirm) clearTimer();
    else {
      pollStatus.value.count = pollStatus.value.count - 1;

      if (pollStatus.value.count <= 0) {
        clearTimer();
        // Every channel that did not reach the target state has failed
        state.value = state.value
          .map(channel => channel.actual === channel.target ? channel : { ...channel, error: true });
      }
    }
  } catch (err) {
    console.info('Error polling relay');
    pollStatus.value.count = pollStatus.value.count - 1;
  }
};

const toggleState = () => {
  const channel = state.value[channelIndex.value];
  if (!channel) return;
  channel.target = !channel.actual;
  isPending.value = true;

  emit('setState', state.value.map(channel => channel.target));

  if (!pollStatus.value) pollingEnable();
  else pollStatus.value.count = POLL_COUNT;
};

const resetChannel = () => {
  const index = channelIndex.value;
  const stateAtIndex = state.value[index];
  if (!stateAtIndex) return;
  stateAtIndex.reset = true;

  setTimeout(async () => {
    const currentState = await props.onPoll();

    const channelValue = currentState[index];
    state.value[index] = {
      actual: channelValue ?? false,
      target: channelValue ?? false,
      error: false,
      reset: false,
    };
  }, RESET_DELAY);
};

const clickHandler = () => {
  if (!state.value.length || isPending.value) return;

  const channel = state.value[channelIndex.value];
  if (!channel) return;

  if (channel.error) resetChannel();
  else toggleState();
};

const selectChannel = (index: number) => {
  if (!isPending.value) channelIndex.value = index;
};

const statusText: Ref<string> = computed(() => {
  const channelState = state.value[channelIndex.value];

  if (channelState === undefined) return '';

  if (channelState.reset) return t('relay.reset');
  else if (channelState.error) return t('relay.error');
  else if (channelState.actual && !channelState.target) return t('relay.pending');
  else if (!channelState.actual && channelState.target) return t('relay.pending');
  else if (channelState.actual && channelState.target) return '';
  else if (!channelState.actual && !channelState.target) return '';
  else return t('relay.initializing');
});

const initialize = (channels: Array<boolean>) => {
  if (pollStatus.value) {
    clearTimer();
  }

  if (props.channels?.length) {
    state.value = channels.map(state => ({ actual: state, target: state, error: false, reset: false }));
  } else {
    state.value = [];
  }
};

onUnmounted(() => {
  clearTimer();
});

watch(() => props.channels, initialize, { immediate: true });
</script>

<template>
  <div class="w-full h-full flex flex-col">
    <div class="flex flex-1 flex-row flex-wrap w-full justify-evenly items-center">
      <div class="grow max-w-[60px] min-w-[20px]">
        <OneCircledIcon
          v-if="state.length >= 2"
          class="
          text-foreground
          transition-all
          duration-200
        "
          :class="{ 'opacity-50': channelIndex != 0, 'cursor-pointer': !isPending, 'cursor-wait': isPending }"
          @click="selectChannel(0)"
        />
      </div>

      <div class="flex flex-col items-center justify-center">
        <slot
          name="button"
          :pending="isPending"
          :state="currentState"
          :click="clickHandler"
        />
        <div
          v-if="currentState"
          class="h-2 -mt-1 text-muted-foreground text-xxs"
          :class="{ 'animate-blink': currentState.actual !== currentState.target }"
        >
          {{ statusText }}
        </div>
      </div>

      <div class="grow max-w-[60px] min-w-[20px]">
        <TwoCircledIcon
          v-if="state.length >= 2"
          class="
          text-foreground
          transition-all
          duration-200
        "
          :class="{ 'opacity-50': channelIndex != 1, 'cursor-pointer': !isPending, 'cursor-wait': isPending }"
          @click="selectChannel(1)"
        />
      </div>
    </div>
    <DeviceMeta
      v-if="props.meta"
      v-bind="props.meta"
    />
  </div>
</template>
